/**
 * WebAuthn (passkey / biometric) helpers — Feb 2026.
 *
 * Three exports:
 *   isPlatformAuthenticatorAvailable(): Promise<boolean>
 *     — cheap capability check. Use to decide whether to show the
 *       "Continue with Face ID" button in the sign-in modal.
 *
 *   enrollPasskey(session): Promise<void>
 *     — authenticated. Fetches creation options from backend, calls
 *       navigator.credentials.create(), posts the attestation back.
 *
 *   signinWithPasskey(identifier?): Promise<{ token_hash: string }>
 *     — public. Fetches request options, calls navigator.credentials.get(),
 *       posts the assertion back. Returns a Supabase magiclink token_hash
 *       that the caller feeds to supabase.auth.verifyOtp().
 *
 * Base64url helpers are hand-rolled because we intentionally avoid
 * pulling a dependency for this. WebAuthn IDs are typically short.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function b64uToBytes(b64u) {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
function bytesToB64u(buf) {
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function isPlatformAuthenticatorAvailable() {
  if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
  try {
    return !!(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.());
  } catch (_) {
    return false;
  }
}

/** Convert the JSON options returned by the backend into what the browser expects. */
function inflateCreationOptions(opts) {
  return {
    ...opts,
    challenge: b64uToBytes(opts.challenge),
    user: { ...opts.user, id: b64uToBytes(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64uToBytes(c.id),
    })),
  };
}
function inflateRequestOptions(opts) {
  return {
    ...opts,
    challenge: b64uToBytes(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64uToBytes(c.id),
    })),
  };
}

function deflateCredentialForServer(cred) {
  const resp = cred.response;
  const attestation = resp.attestationObject
    ? {
        clientDataJSON: bytesToB64u(resp.clientDataJSON),
        attestationObject: bytesToB64u(resp.attestationObject),
        transports: typeof resp.getTransports === "function" ? resp.getTransports() : undefined,
      }
    : {
        clientDataJSON: bytesToB64u(resp.clientDataJSON),
        authenticatorData: bytesToB64u(resp.authenticatorData),
        signature: bytesToB64u(resp.signature),
        userHandle: resp.userHandle ? bytesToB64u(resp.userHandle) : null,
      };
  return {
    id: cred.id, // already base64url
    rawId: bytesToB64u(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment,
    response: attestation,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

export async function enrollPasskey(session, { deviceLabel } = {}) {
  const label = deviceLabel || _guessDeviceLabel();
  const r1 = await fetch(`${API}/auth/webauthn/register/options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
  });
  if (!r1.ok) throw new Error("Could not get registration options");
  const payload = await r1.json();
  const opts = typeof payload.options === "string" ? JSON.parse(payload.options) : payload.options;
  const publicKey = inflateCreationOptions(opts);
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("Enrollment cancelled");
  const r2 = await fetch(`${API}/auth/webauthn/register/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      credential: deflateCredentialForServer(cred),
      device_label: label,
    }),
  });
  if (!r2.ok) {
    const data = await r2.json().catch(() => ({}));
    throw new Error(data.detail || "Registration failed");
  }
  return await r2.json();
}

export async function signinWithPasskey(identifier) {
  const r1 = await fetch(`${API}/auth/webauthn/login/options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier: (identifier || "").trim() }),
  });
  if (!r1.ok) throw new Error("Could not get login options");
  const payload = await r1.json();
  if (!payload.has_credentials) {
    throw new Error("No passkey registered for this account on this device");
  }
  const opts = typeof payload.options === "string" ? JSON.parse(payload.options) : payload.options;
  const publicKey = inflateRequestOptions(opts);
  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error("Sign-in cancelled");
  const r2 = await fetch(`${API}/auth/webauthn/login/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      credential: deflateCredentialForServer(assertion),
      identifier: (identifier || "").trim() || null,
    }),
  });
  if (!r2.ok) {
    const data = await r2.json().catch(() => ({}));
    throw new Error(data.detail || "Verification failed");
  }
  return await r2.json();
}

/** Best-effort friendly label based on UA — user can rename in Account → Security. */
function _guessDeviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iPhone / iPad";
  if (/Android/i.test(ua)) return "Android device";
  if (/Mac/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows PC";
  return "This device";
}
